import React, { useState, useEffect, useMemo } from 'react';
import KondoSidebarMenu from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebaseConfig';
// eslint-disable-next-line no-unused-vars
import { collection, query, where, onSnapshot, orderBy, doc, updateDoc, deleteDoc, writeBatch } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import {
  IoArrowBack,
  IoAdd,
  IoPencil,
  IoTrash,
  IoCaretUpOutline,
  IoCaretDownOutline,
  IoFunnelOutline,
  IoClose,
  IoAlertCircleOutline,
  IoWalletOutline,
  IoCalendarOutline
} from 'react-icons/io5';
import moment from 'moment';
import './GestionCuotasScreen.css';

// Componente EstatusCargo
const EstatusCargo = ({ estatus, cargo }) => {
  let className = 'cuota-status-badge';
  let text = estatus.charAt(0).toUpperCase() + estatus.slice(1);

  if (estatus === 'pendiente') {
    const ahora = new Date();
    const vencimiento = cargo?.fechaVencimiento?.toDate 
      ? cargo.fechaVencimiento.toDate() 
      : (cargo?.fechaVencimiento ? new Date(cargo.fechaVencimiento) : null);
      
    const estaVencido = vencimiento && vencimiento < ahora;
    if (estaVencido) {
      className += ' vencido';
      text = 'Vencido';
    } else {
      className += ' pendiente-pago';
    }
  } else if (estatus === 'pagado') {
    className += ' pagado';
  } else if (estatus === 'cancelado') {
    className += ' cancelado';
  } else if (estatus === 'parcial') {
    className += ' parcial';
  }
  return <span className={className}>{text}</span>;
};

// --- OPTIMIZATION: Extract Modal to avoid re-rendering the huge table on input change ---
const DeleteCargoModal = ({ isOpen, onClose, onConfirm, count, isSingle }) => {
  const [reason, setReason] = useState('Error administrativo');
  const [detail, setDetail] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setReason('Error administrativo');
      setDetail('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <IoAlertCircleOutline color="#dc2626" size={24} />
            <h3 style={{ margin: 0 }}>Cancelar Cargo</h3>
          </div>
          <IoClose size={24} style={{ cursor: 'pointer', color: '#6b7280' }} onClick={onClose} />
        </div>

        <p style={{ color: '#4b5563', marginBottom: '20px' }}>
          ¿Estás seguro que deseas cancelar {(!isSingle && count > 1) ? `estos ${count} cargos` : 'este cargo'}? Esta acción no se puede deshacer y el saldo de los residentes será ajustado.
        </p>

        <div className="form-group">
          <label className="form-label">Motivo de Cancelación</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="form-select"
          >
            <option value="Error administrativo">Error administrativo</option>
            <option value="Convenio">Convenio</option>
            <option value="Otro">Otro</option>
          </select>
        </div>

        {(reason === 'Otro' || reason === 'Error administrativo' || reason === 'Convenio') && (
          <div className="form-group">
            <label className="form-label">Detalles Adicionales {reason === 'Otro' && '(Obligatorio)'}</label>
            <input
              type="text"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
              className="form-input"
              placeholder="Especifique..."
              autoFocus // Auto-focus here is safe now
            />
          </div>
        )}

        <div className="modal-actions">
          <button onClick={onClose} className="form-button secondary">Cancelar</button>
          <button
            onClick={() => onConfirm(reason, detail)}
            className="form-button danger"
          >
            Confirmar Cancelación
          </button>
        </div>
      </div>
    </div>
  );
};

function GestionCuotasScreen() {
  const navigate = useNavigate();
  const { userData } = useAuth();
  const [cargos, setCargos] = useState([]);
  const [loading, setLoading] = useState(true);

  // Estados para el Modal de Eliminación
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCargoId, setSelectedCargoId] = useState(null);

  // Note: deleteReason states moved to DeleteCargoModal

  // Estados para Bulk Delete
  const [selectedItems, setSelectedItems] = useState([]);

  // Estados para Ordenamiento y Filtros Avanzados
  const [sortConfig, setSortConfig] = useState({ key: 'fechaCreacion', direction: 'desc' });
  const [columnFilters, setColumnFilters] = useState({
    estatus: { value: '', operator: 'contains' },
    unidadResidente: { value: '', operator: 'contains' },
    nombreResidente: { value: '', operator: 'contains' },
    descripcion: { value: '', operator: 'contains' },
    monto: { value: '', operator: 'greaterThan' },
    fechaVencimiento: { value: '', operator: 'equals' },
    fechaPago: { value: '', operator: 'equals' }
  });
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    if (!userData?.condominioId) {
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, "cargos"),
      where("condominioId", "==", userData.condominioId),
      orderBy("estatus", "asc"),
      orderBy("fechaCreacion", "desc")
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const listaCargos = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(c => c.origen !== 'marketplace' && c.tipo !== 'Servicio Marketplace'); // HIDE MARKETPLACE

      setCargos(listaCargos);
      setLoading(false);
    }, (error) => {
      console.error("Error al cargar cargos: ", error);
      if (error.code === 'failed-precondition') {
        alert("Falta índice en Firestore. Revisa la consola.");
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [userData]);

  // --- HELPER: Process Batches in Chunks ---
  const processInChunks = async (items, operationCallback, batchSize = 500) => {
    for (let i = 0; i < items.length; i += batchSize) {
      const chunk = items.slice(i, i + batchSize);
      const batch = writeBatch(db);
      chunk.forEach(item => operationCallback(batch, item));
      await batch.commit();
    }
  };

  // --- LÓGICA DE ELIMINACIÓN ---
  const handlePurgeCancelled = async () => {
    const cancelledCargos = cargos.filter(c => c.estatus === 'cancelado');
    if (cancelledCargos.length === 0) {
      alert("No hay cargos cancelados para eliminar.");
      return;
    }

    const confirm = window.confirm(`⚠️ ESTA ACCIÓN ES IRREVERSIBLE ⚠️\n\n¿Estás seguro de que deseas ELIMINAR PERMANENTEMENTE ${cancelledCargos.length} cargos cancelados de la base de datos?\n\nEsto no afectará facturas ni usuarios, solo limpiará los registros de cargos locales.`);

    if (confirm) {
      setLoading(true);
      try {
        await processInChunks(cancelledCargos, (batch, cargo) => {
          const docRef = doc(db, 'cargos', cargo.id);
          batch.delete(docRef);
        });
        alert("Cargos eliminados correctamente.");
      } catch (error) {
        console.error("Error eliminando cargos:", error);
        alert("Error al eliminar los cargos: " + error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(itemId => itemId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const toggleSelectAll = () => {
    const currentIds = currentItems.map(item => item.id);
    const allCurrentSelected = currentIds.every(id => selectedItems.includes(id));

    if (allCurrentSelected) {
      // Deselect all items on current page
      setSelectedItems(prev => prev.filter(id => !currentIds.includes(id)));
    } else {
      // Select all items on current page
      const newSelected = [...selectedItems];
      currentIds.forEach(id => {
        if (!newSelected.includes(id)) {
          newSelected.push(id);
        }
      });
      setSelectedItems(newSelected);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedItems.length === 0) return;
    setShowDeleteModal(true);
  };

  const handleDeleteClick = (cargoId) => {
    setSelectedCargoId(cargoId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async (reason, detail) => {
    if (!selectedCargoId && selectedItems.length === 0) return;

    if (reason === 'Otro' && !detail.trim()) {
      alert("Por favor especifica el motivo.");
      return;
    }

    const motivoFinal = reason === 'Otro' ? detail : (detail ? `${reason}: ${detail}` : reason);

    setLoading(true);

    try {
      if (selectedCargoId) {
        // Delete Single
        const cargoRef = doc(db, "cargos", selectedCargoId);
        await updateDoc(cargoRef, {
          estatus: 'cancelado',
          motivoCancelacion: motivoFinal
        });
      } else {
        // Bulk Delete - Use Chunked Batches
        // selectedItems is array of IDs string
        await processInChunks(selectedItems, (batch, id) => {
          const cargoRef = doc(db, "cargos", id);
          batch.update(cargoRef, {
            estatus: 'cancelado',
            motivoCancelacion: motivoFinal
          });
        });

        setSelectedItems([]); // Clear selection after delete
      }

      setShowDeleteModal(false);
      setSelectedCargoId(null);
    } catch (error) {
      console.error("Error al eliminar cargo(s):", error);
      alert("Error al cancelar los cargos: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // --- LÓGICA DE ORDENAMIENTO Y FILTROS ---
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (column, value, operator = 'contains') => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: { value, operator }
    }));
    setCurrentPage(1); // Reset page on filter change
  };

  const clearFilter = (column) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: { value: '', operator: 'contains' }
    }));
    setActiveFilterColumn(null);
  };

  const getUniqueOptions = (key) => {
    const options = new Set();
    cargos.forEach(item => {
      let val = item[key];
      if (key === 'fechaVencimiento') {
        if (val && val.toDate) val = moment(val.toDate()).format('DD/MM/YYYY');
      }
      if (val) options.add(val);
    });
    return Array.from(options).sort();
  };


  const sortedAndFilteredCargos = useMemo(() => {
    let data = [...cargos];

    // 1. Column Filters
    Object.keys(columnFilters).forEach(key => {
      const filter = columnFilters[key];
      if (filter.value) {
        data = data.filter(item => {
          let itemVal = item[key];

          // Handle Date Objects
          if (key === 'fechaVencimiento' || key === 'fechaPago') {
            if (!itemVal || !itemVal.toDate) return false;
            // Compare using Moment for dates
            const dateVal = moment(itemVal.toDate());
            const filterDate = moment(filter.value, 'YYYY-MM-DD');

            if (!filterDate.isValid()) return true;

            if (filter.operator === 'equals') return dateVal.isSame(filterDate, 'day');
            if (filter.operator === 'greaterThan') return dateVal.isAfter(filterDate, 'day');
            if (filter.operator === 'lessThan') return dateVal.isBefore(filterDate, 'day');
            return dateVal.format('DD/MM/YYYY').includes(filter.value);
          }

          // Handle Strings & Numbers
          if (typeof itemVal === 'string') {
            itemVal = itemVal.toLowerCase();
            const filterVal = filter.value.toLowerCase();

            if (filter.operator === 'equals') return itemVal === filterVal;
            if (filter.operator === 'contains') return itemVal.includes(filterVal);
            if (filter.operator === 'startsWith') return itemVal.startsWith(filterVal);
            if (filter.operator === 'endsWith') return itemVal.endsWith(filterVal);
            if (filter.operator === 'notContains') return !itemVal.includes(filterVal);
          } else if (typeof itemVal === 'number') {
            const filterNum = parseFloat(filter.value);
            if (isNaN(filterNum)) return true;

            if (filter.operator === 'equals') return itemVal === filterNum;
            if (filter.operator === 'greaterThan') return itemVal > filterNum;
            if (filter.operator === 'lessThan') return itemVal < filterNum;
            // Convert to string for contains
            return itemVal.toString().includes(filter.value);
          }

          return true;
        });
      }
    });

    // 2. Sorting
    if (sortConfig.key) {
      data.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle special fields
        if (sortConfig.key === 'fechaVencimiento' || sortConfig.key === 'fechaPago' || sortConfig.key === 'fechaCreacion') {
          aVal = aVal?.toMillis ? aVal.toMillis() : 0;
          bVal = bVal?.toMillis ? bVal.toMillis() : 0;
        } else if (sortConfig.key === 'monto') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        } else {
          aVal = (aVal || '').toString().toLowerCase();
          bVal = (bVal || '').toString().toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [cargos, columnFilters, sortConfig]);

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedAndFilteredCargos.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedAndFilteredCargos.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="ks-page">
      <KondoSidebarMenu activeRoute="/admin-cuotas" />

      <main className="ks-main">
        <KondoBrandHeader title="Administración" showBackButton={false} />

        <div className="ks-scroll-area">
          <div className="cuotas-container">
            {/* --- HERO BANNER STANDARD --- */}
            <div className="muro-header-stitch">
              <div className="muro-hero-card">
                <div className="muro-hero-content">
                  <h1>Gestión de Cuotas</h1>
                  <p>Administra los cargos, pagos y el estado de cuenta de los residentes.</p>

                  <div className="muro-hero-actions-row">
                    <button
                      onClick={() => navigate('/admin-cuotas/crear')}
                      className="muro-hero-action-btn"
                    >
                      <IoAdd size={20} />
                      <span>Crear Cargo</span>
                    </button>

                    <button
                      onClick={() => navigate('/admin-cuotas/configuracion-automatica')}
                      className="muro-hero-action-btn"
                      title="Configurar cuota ordinaria y automatización"
                    >
                      <IoCalendarOutline size={18} />
                      <span>Cuota Ordinaria / Automática</span>
                    </button>

                    {selectedItems.length > 0 && (
                      <button
                        onClick={handleBulkDeleteClick}
                        className="muro-hero-action-btn secondary"
                      >
                        <IoTrash size={18} />
                        <span>Cancelar ({selectedItems.length})</span>
                      </button>
                    )}
                  </div>
                </div>
                <IoWalletOutline className="muro-hero-icon-decor" />
              </div>
            </div>

            <div className="dashboard-list">
              {/* Filters Bar */}
              <div className="filters-bar">
                {/* Left side: Active Filter Pills (Visual feedback) */}
                <div className="filter-pills-row">
                  {Object.keys(columnFilters).map(key => {
                    if (!columnFilters[key].value) return null;
                    return (
                      <div key={key} className="filter-pill active" onClick={() => {
                        setActiveFilterColumn(activeFilterColumn === key ? null : key);
                      }}>
                        {key.charAt(0).toUpperCase() + key.slice(1)}: {columnFilters[key].operator === 'equals' ? '=' : ''} {columnFilters[key].value}
                        <IoClose style={{ marginLeft: '5px', verticalAlign: 'middle' }} onClick={(e) => {
                          e.stopPropagation();
                          clearFilter(key);
                        }} />
                      </div>
                    );
                  })}
                  {Object.keys(columnFilters).every(key => !columnFilters[key].value) && (
                    <span className="no-filters-text">No hay filtros activos</span>
                  )}
                </div>

                {/* Right side: Rows per page selector and Top Pagination */}
                <div className="pagination-controls-top">
                  <div className="items-per-page">
                    <span>Mostrar:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="form-select"
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={sortedAndFilteredCargos.length}>Todos ({sortedAndFilteredCargos.length})</option>
                    </select>
                  </div>

                  {/* Top Pagination Controls */}
                  <div className="pagination-arrows">
                    <button
                      onClick={() => paginate(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="page-btn-icon"
                      title="Página anterior"
                    >
                      &lt;
                    </button>
                    <button
                      onClick={() => paginate(currentPage + 1)}
                      disabled={currentPage === totalPages || totalPages === 0}
                      className="page-btn-icon"
                      title="Siguiente página"
                    >
                      &gt;
                    </button>
                  </div>
                </div>
              </div>

              {loading && <p className="loading-state">Cargando transacciones...</p>}

              {!loading && cargos.length === 0 && (
                <p className="empty-state">No hay cargos generados en este condominio.</p>
              )}

              {!loading && cargos.length > 0 && (
                <div>
                  <div className="table-responsive hide-on-mobile">
                    <table className="modern-table">
                      <thead>
                        <tr>
                          <th style={{ width: '40px', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={currentItems.length > 0 && currentItems.every(item => selectedItems.includes(item.id))}
                              onChange={toggleSelectAll}
                              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                              title="Seleccionar visible"
                            />
                          </th>
                          {[
                            { key: 'estatus', label: 'Estatus', width: '100px' },
                            { key: 'unidadResidente', label: 'Unidad', width: '100px' },
                            { key: 'nombreResidente', label: 'Residente', width: '150px' },
                            { key: 'descripcion', label: 'Descripción', width: '200px' },
                            { key: 'monto', label: 'Monto', width: '100px' },
                            { key: 'fechaVencimiento', label: 'Vencimiento', width: '120px' },
                            { key: 'fechaPago', label: 'Pagado', width: '120px' }
                          ].map(({ key, label, width, noFilter }) => (
                            <th key={key} style={{ minWidth: width, position: 'relative', cursor: 'pointer' }}>
                              <div className="th-content" onClick={() => handleSort(key)}>
                                {label}
                                <div className="sort-icons">
                                  {sortConfig.key === key && sortConfig.direction === 'asc' ? <IoCaretUpOutline color="#7B61FF" /> : <IoCaretUpOutline color="#d1d5db" size={10} />}
                                  {sortConfig.key === key && sortConfig.direction === 'desc' ? <IoCaretDownOutline color="#7B61FF" /> : <IoCaretDownOutline color="#d1d5db" size={10} />}
                                </div>
                              </div>
                              {!noFilter && (
                                <IoFunnelOutline
                                  size={14}
                                  className={`filter-icon-trigger ${columnFilters[key].value ? 'active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveFilterColumn(activeFilterColumn === key ? null : key);
                                  }}
                                />
                              )}

                              {/* FILTER POPOVER */}
                              {activeFilterColumn === key && (
                                <div className="filter-popover" onClick={(e) => e.stopPropagation()}>
                                  <div className="filter-popover-header">Filtrar {label}</div>

                                  {key !== 'estatus' && (
                                    <select
                                      className="filter-operator-select"
                                      value={columnFilters[key].operator}
                                      onChange={(e) => handleFilterChange(key, columnFilters[key].value, e.target.value)}
                                    >
                                      {key === 'monto' || key === 'fechaVencimiento' || key === 'fechaPago' ? (
                                        <>
                                          <option value="equals">Es igual a</option>
                                          <option value="greaterThan">Mayor que</option>
                                          <option value="lessThan">Menor que</option>
                                        </>
                                      ) : (
                                        <>
                                          <option value="contains">Contiene</option>
                                          <option value="equals">Es igual a</option>
                                          <option value="startsWith">Empieza con</option>
                                          <option value="notContains">No contiene</option>
                                        </>
                                      )}
                                    </select>
                                  )}

                                  {key === 'estatus' ? (
                                    <select
                                      className="filter-value-input"
                                      value={columnFilters[key].value}
                                      onChange={(e) => handleFilterChange(key, e.target.value, 'equals')}
                                    >
                                      <option value="">Todos los Estatus</option>
                                      {getUniqueOptions(key).map(opt => (
                                        <option key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</option>
                                      ))}
                                    </select>
                                  ) : (
                                    <input
                                      type={key === 'fechaVencimiento' || key === 'fechaPago' ? 'date' : (key === 'monto' ? 'number' : 'text')}
                                      placeholder={key === 'monto' ? 'Ej. 1000' : 'Buscar...'}
                                      className="filter-value-input"
                                      value={columnFilters[key].value}
                                      onChange={(e) => handleFilterChange(key, e.target.value, columnFilters[key].operator)}
                                      autoFocus
                                    />
                                  )}

                                  <div className="filter-popover-actions">
                                    <button
                                      onClick={() => { clearFilter(key); setActiveFilterColumn(null); }}
                                      className="btn-clear-filter"
                                    >
                                      Limpiar
                                    </button>
                                    <button
                                      onClick={() => setActiveFilterColumn(null)}
                                      className="btn-close-filter"
                                    >
                                      Cerrar
                                    </button>
                                  </div>
                                </div>
                              )}
                            </th>
                          ))}
                          <th>Editar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((cargo) => (
                          <tr key={cargo.id} className={selectedItems.includes(cargo.id) ? 'selected-row' : ''}>
                            <td style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(cargo.id)}
                                onChange={() => toggleSelectItem(cargo.id)}
                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                              />
                            </td>
                             <td><EstatusCargo estatus={cargo.estatus} cargo={cargo} /></td>
                            <td style={{ fontWeight: '600' }}>{cargo.unidadResidente || 'N/A'}</td>
                            <td>{cargo.nombreResidente || 'N/A'}</td>
                            <td>
                              <span className="cargo-tipo-text">{cargo.tipo}</span>
                              <span className="cargo-desc-text">
                                {cargo.descripcion && cargo.descripcion.length > 30 ? cargo.descripcion.substring(0, 30) + '...' : cargo.descripcion}
                              </span>
                            </td>
                            <td className="cargo-monto-text">
                              ${new Intl.NumberFormat('es-MX').format(cargo.monto)}
                            </td>
                            <td>{moment(cargo.fechaVencimiento?.toDate ? cargo.fechaVencimiento.toDate() : new Date()).format('D MMM YYYY')}</td>
                            <td>
                              {cargo.fechaPago
                                ? moment(cargo.fechaPago.toDate()).format('D MMM YYYY')
                                : <span className="no-date-text">-</span>}
                            </td>
                            <td>
                              {cargo.estatus === 'pendiente' || cargo.estatus === 'parcial' ? (
                                <div className="actions-cell">
                                  <button
                                    className="small-action-btn edit"
                                    onClick={() => navigate(`/admin-cuotas/editar/${cargo.id}`)}
                                    title="Editar"
                                  >
                                    <IoPencil size={16} />
                                  </button>
                                  <button
                                    className="small-action-btn delete"
                                    onClick={() => handleDeleteClick(cargo.id)}
                                    title="Cancelar Cargo"
                                  >
                                    <IoTrash size={16} />
                                  </button>
                                </div>
                              ) : (
                                <span className="closed-status-text">Cerrado</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Vista Móvil: Tarjetas Premium Responsivas */}
                  <div className="cuotas-mobile-list show-on-mobile-flex">
                    {currentItems.map((cargo) => (
                      <div 
                        key={cargo.id} 
                        className={`cuota-card-premium ${selectedItems.includes(cargo.id) ? 'selected' : ''}`}
                      >
                        {/* Cabecera de la Tarjeta */}
                        <div className="cuota-card-header">
                          <div className="cuota-card-header-left">
                            <input
                              type="checkbox"
                              checked={selectedItems.includes(cargo.id)}
                              onChange={() => toggleSelectItem(cargo.id)}
                              className="cuota-card-checkbox"
                            />
                            <span className="cuota-card-unidad">Casa {cargo.unidadResidente || 'N/A'}</span>
                          </div>
                           <EstatusCargo estatus={cargo.estatus} cargo={cargo} />
                        </div>

                        {/* Cuerpo de la Tarjeta */}
                        <div className="cuota-card-body">
                          <div className="cuota-card-icon-wrapper">
                            <IoWalletOutline size={20} className="cuota-card-icon" />
                          </div>
                          <div className="cuota-card-info">
                            <span className="cuota-card-residente">{cargo.nombreResidente || 'N/A'}</span>
                            <span className="cuota-card-tipo">{cargo.tipo}</span>
                            {cargo.descripcion && (
                              <p className="cuota-card-desc">{cargo.descripcion}</p>
                            )}
                          </div>
                        </div>

                        {/* Fila Financiera */}
                        <div className="cuota-card-financial-row">
                          <div className="cuota-card-dates">
                            <div className="date-item">
                              <span className="date-label">Vencimiento:</span>
                              <span className="date-val">{moment(cargo.fechaVencimiento?.toDate ? cargo.fechaVencimiento.toDate() : new Date()).format('D MMM YYYY')}</span>
                            </div>
                            {cargo.fechaPago && (
                              <div className="date-item">
                                <span className="date-label">Pagado el:</span>
                                <span className="date-val highlighted">{moment(cargo.fechaPago.toDate()).format('D MMM YYYY')}</span>
                              </div>
                            )}
                          </div>
                          <div className="cuota-card-monto">
                            ${new Intl.NumberFormat('es-MX').format(cargo.monto)}
                          </div>
                        </div>

                        {/* Fila de Acciones */}
                        <div className="cuota-card-actions">
                          {cargo.estatus === 'pendiente' || cargo.estatus === 'parcial' ? (
                            <div className="actions-buttons">
                              <button
                                className="premium-action-btn edit"
                                onClick={() => navigate(`/admin-cuotas/editar/${cargo.id}`)}
                                title="Editar"
                              >
                                <IoPencil size={15} /> Editar
                              </button>
                              <button
                                className="premium-action-btn delete"
                                onClick={() => handleDeleteClick(cargo.id)}
                                title="Cancelar Cargo"
                              >
                                <IoTrash size={15} /> Cancelar
                              </button>
                            </div>
                          ) : (
                            <span className="cuota-card-closed-badge">Cerrado</span>
                          )}
                        </div>

                      </div>
                    ))}
                  </div>

                  {/* Controls Pagination Bottom */}
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Mostrando {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, sortedAndFilteredCargos.length)} de {sortedAndFilteredCargos.length} registros
                    </div>
                    <div className="pagination-controls-bottom">
                      <span className="page-indicator">
                        Página {currentPage} de {totalPages || 1}
                      </span>
                      <div className="pagination-btns">
                        <button
                          onClick={() => paginate(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="page-btn"
                        >
                          &lt; Anterior
                        </button>
                        <button
                          onClick={() => paginate(currentPage + 1)}
                          disabled={currentPage === totalPages || totalPages === 0}
                          className="page-btn"
                        >
                          Siguiente &gt;
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <DeleteCargoModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedCargoId(null);
        }}
        onConfirm={confirmDelete}
        count={selectedItems.length}
        isSingle={!!selectedCargoId}
      />

      <KondoBottomNav activeRoute="/admin-cuotas" />
    </div>
  );
}

export default GestionCuotasScreen;